import type { LoaderFunctionArgs } from "react-router-dom";
import { Link, useLoaderData } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, Share2 } from "lucide-react";
import { motion, useScroll, useSpring } from "motion/react";
import { FadeIn, Section } from "../components/ui";
import { NewsletterForm } from "../components/NewsletterForm";
import { Seo } from "../components/Seo";
import { SITE_URL } from "../lib/site";
import {
  fetchForRoute,
  getPost,
  getPostCategory,
  getFeaturedImage,
  calculateReadTime,
  formatDate,
  stripHtml,
  type WPPost,
} from "../lib/wordpress";

export async function loader({ params }: LoaderFunctionArgs) {
  return fetchForRoute(`/blog/${params.slug}`, () => getPost(params.slug!));
}

export function BlogPost() {
  const post = useLoaderData() as WPPost;

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const handleShare = async () => {
    const shareData = {
      title: post.title.rendered,
      text: "Confira este insight da P2A Marketing:",
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link copiado para a área de transferência!");
      }
    } catch (err) {
      console.error("Erro ao compartilhar:", err);
    }
  };

  return (
    <main className="relative z-10 pt-24 md:pt-32 pb-24">
      <Seo
        title={`${stripHtml(post.title.rendered)} | P2A Insights`}
        description={stripHtml(post.excerpt.rendered).slice(0, 160)}
        path={`/blog/${post.slug}/`}
        image={getFeaturedImage(post) || undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: stripHtml(post.title.rendered),
          description: stripHtml(post.excerpt.rendered).slice(0, 160),
          image: getFeaturedImage(post) || undefined,
          datePublished: post.date,
          author: { "@type": "Organization", name: "P2A Marketing & Branding" },
          publisher: { "@type": "Organization", name: "P2A Marketing & Branding" },
          mainEntityOfPage: `${SITE_URL}/blog/${post.slug}/`,
        }}
      />
      {/* Reading Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-brand-accent z-50 origin-[0%]"
        style={{ scaleX }}
      />

      <Section className="py-8">
        <FadeIn>
          <Link to="/blog" className="inline-flex items-center gap-2 text-brand-light/50 hover:text-brand-accent transition-all font-bold uppercase tracking-widest text-xs mb-8 md:mb-12 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Voltar para Insights</span>
          </Link>
        </FadeIn>

        <article className="max-w-5xl mx-auto">
          <FadeIn>
            <header className="mb-12 md:mb-16">
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <div className="flex items-center gap-4 text-brand-light/40 text-xs font-bold uppercase tracking-widest">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {formatDate(post.date)}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-brand-light/20"></span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    {calculateReadTime(post.content.rendered)}
                  </span>
                </div>
              </div>

              <h1
                className="text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95] md:leading-[0.9] text-white mb-8"
                dangerouslySetInnerHTML={{ __html: post.title.rendered }}
              />
            </header>
          </FadeIn>

          {getFeaturedImage(post) && (
            <FadeIn delay={0.1}>
              <div className="relative w-full aspect-[16/9] md:aspect-[21/9] rounded-[2rem] md:rounded-[3rem] overflow-hidden border border-white/10 mb-16 shadow-2xl group">
                <img
                  src={getFeaturedImage(post)}
                  alt={post.title.rendered}
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/40 to-transparent pointer-events-none" />
              </div>
            </FadeIn>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-16">
            <FadeIn delay={0.2}>
              <div
                className="prose-wp max-w-screen-md"
                dangerouslySetInnerHTML={{ __html: post.content.rendered }}
              />

              {/* Post Footer/CTA */}
              <div className="mt-24 pt-12 border-t border-white/10">
                <div className="bg-brand-accent/5 rounded-[2.5rem] p-8 md:p-12 border border-brand-accent/20 flex flex-col md:flex-row items-center justify-between gap-8 group hover:bg-brand-accent/10 transition-colors">
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tighter text-white mb-2">Pronto para acelerar?</h3>
                    <p className="text-brand-light/60">Vamos transformar sua visão em resultados exponenciais.</p>
                  </div>
                  <a
                    href="https://wa.me/5524993144981?text=Vim%20do%20site%20gostaria%20de%20saber%20sobre%20os%20servi%C3%A7os"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap px-8 py-4 bg-brand-accent text-white font-bold uppercase tracking-widest rounded-full hover:bg-white hover:text-brand-dark transition-all scale-100 hover:scale-105 active:scale-95"
                  >
                    Falar com Especialista
                  </a>
                </div>
              </div>
            </FadeIn>

            {/* Sidebar Desktop Only */}
            <aside className="hidden lg:block w-64 sticky top-32 h-fit">
              <div className="space-y-12">
                <div>
                  <h4 className="text-white font-black uppercase tracking-widest text-xs mb-6 pb-2 border-b border-white/10">Compartilhar</h4>
                  <div className="flex gap-4">
                    <button
                      onClick={handleShare}
                      className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-brand-light/50 hover:bg-brand-accent hover:border-brand-accent hover:text-white transition-all cursor-pointer"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="text-white font-black uppercase tracking-widest text-xs mb-6 pb-2 border-b border-white/10">P2A Insights</h4>
                  <p className="text-brand-light/50 text-sm leading-relaxed">
                    Conteúdo exclusivo sobre estratégia, tecnologia e branding para líderes que não se contentam com o comum.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </Section>
      <NewsletterForm />
    </main>
  );
}
